/**
 * SQLite Script Provider
 *
 * Implements SQL script generation for SQLite.
 */

import type {
  ScriptProvider,
  TableScriptType,
  TableScriptContext,
  DatabaseScriptType,
  DatabaseScriptContext,
  ColumnMetadata,
  NewObjectTemplateContext,
} from '../types/scriptProvider';
import { SELECT_TOP_ROW_LIMIT } from '../selectTopRowLimit';
import { appendIndexStatements } from './indexStatements';
import { resolvePrimaryKey } from './primaryKeyClause';

function quoteIdentifier(name: string): string {
  return `"${name.replace(/"/g, '""')}"`;
}

function qualifyTableName(schema: string, table: string): string {
  if (!schema) return quoteIdentifier(table);
  return `${quoteIdentifier(schema)}.${quoteIdentifier(table)}`;
}

function normalizeStatement(sql: string): string {
  const trimmed = sql.trim();
  if (!trimmed) return trimmed;
  return trimmed.endsWith(';') ? trimmed : `${trimmed};`;
}

function formatInsertPlaceholder(col: ColumnMetadata): string {
  const type = col.dataType.toUpperCase();

  if (type.includes('INT') || type.includes('REAL') || type.includes('FLOA') || type.includes('DOUB')) {
    return '0';
  }

  if (type.includes('BOOL')) {
    return '0';
  }

  if (type.includes('DATE') || type.includes('TIME')) {
    return 'CURRENT_TIMESTAMP';
  }

  if (type.includes('BLOB')) {
    return `X''`;
  }

  return `''`;
}

/** The names `sqliteMetadataService` gives the SYNTHETIC primary-key column it
 *  adds for a table that declares none (`pickRowIdAlias`). That column is the
 *  implicit rowid, not a declared one, so it must never be re-emitted. */
const ROWID_ALIASES = new Set(['rowid', '_rowid_', 'oid']);

/**
 * CREATE TABLE rendered from column metadata — used when `sqlite_master.sql` is
 * unavailable, and by the Table Designer's legacy-draft rebase, which strips
 * `createSql` and renders BOTH of its buffers through here. The primary key
 * therefore has to survive the render: while it did not, a restored draft whose
 * only edit was toggling `isPrimaryKey` produced two identical buffers and was
 * discarded as clean.
 */
function generateCreateFallback(ctx: TableScriptContext): string {
  const fullName = qualifyTableName(ctx.schema, ctx.tableName);
  const columns = (ctx.metadata?.columns ?? []).filter(
    (col) => !(col.isIdentity && ROWID_ALIASES.has(col.name.toLowerCase())),
  );

  if (!columns.length) {
    return `-- CREATE TABLE script for ${fullName}\n-- No SQLite DDL available (sqlite_master.sql was NULL)\nCREATE TABLE ${fullName} (\n  -- Column definitions would appear here\n);\n`;
  }

  // Only a LONE INTEGER primary key aliases the rowid, and only in the inline
  // form — `PRIMARY KEY (id)` as a table constraint builds a separate index
  // instead, and AUTOINCREMENT is a syntax error anywhere else. Every other
  // key (composite, or a non-INTEGER column) is emitted as a table constraint.
  const pkColumns = columns.filter((col) => col.isPrimaryKey);
  const inlinePk =
    pkColumns.length === 1 && pkColumns[0].dataType.trim().toUpperCase() === 'INTEGER'
      ? pkColumns[0]
      : undefined;

  const columnLines = columns.map((col) => {
    const defaultValue = col.defaultValue ? ` DEFAULT ${col.defaultValue}` : '';
    // A typeless column really declares no type (`PRAGMA table_info.type` is
    // `''`), and SQLite derives a column's AFFINITY from whatever word stands
    // there — so the slot is left out rather than filled with a placeholder.
    const declaration = `  ${quoteIdentifier(col.name)}${col.dataType ? ` ${col.dataType}` : ''}`;
    if (col === inlinePk) {
      // An INTEGER PRIMARY KEY is the rowid: implicitly NOT NULL, so the
      // nullability keyword is left off exactly as SQLite's own DDL does.
      return `${declaration} PRIMARY KEY${col.isIdentity ? ' AUTOINCREMENT' : ''}${defaultValue}`;
    }
    return `${declaration} ${col.isNullable ? 'NULL' : 'NOT NULL'}${defaultValue}`;
  });

  // The table-constraint form lists the key in the catalog's declared order
  // (`PRAGMA table_info.pk` ordinals, see `resolvePrimaryKey`), not in
  // table-column order — the same rule every other engine's baseline follows.
  const declaredKey = ctx.metadata ? resolvePrimaryKey(ctx.metadata, '') : undefined;
  const keyColumns = declaredKey !== undefined
    && declaredKey.columns.length === pkColumns.length
    && pkColumns.every((col) => declaredKey.columns.includes(col.name))
    ? declaredKey.columns
    : pkColumns.map((col) => col.name);
  const pkConstraint =
    inlinePk || pkColumns.length === 0
      ? ''
      : `,\n  PRIMARY KEY (${keyColumns.map((name) => quoteIdentifier(name)).join(', ')})`;

  return `CREATE TABLE ${fullName} (\n${columnLines.join(',\n')}${pkConstraint}\n);\n`;
}

export const sqliteScriptProvider: ScriptProvider = {
  engine: 'sqlite',

  quoteIdentifier,
  qualifyTableName,

  generateTableScript(type: TableScriptType, ctx: TableScriptContext): string {
    const fullName = qualifyTableName(ctx.schema, ctx.tableName);

    switch (type) {
      case 'create': {
        const createSql = ctx.metadata?.createSql;
        const table = typeof createSql === 'string' && createSql.trim().length > 0
          ? normalizeStatement(createSql)
          : generateCreateFallback(ctx);
        // The table's indexes follow as CREATE INDEX statements — the
        // Designer's Indexes tab reads them from the buffer (see
        // `indexStatements.ts`). `sqlite_master` holds the columns, the
        // UNIQUE flag and the partial `WHERE` of every declared index.
        return ctx.metadata ? appendIndexStatements(table, ctx.metadata, { quote: quoteIdentifier, table: fullName }) : table;
      }

      case 'drop':
        return `DROP TABLE IF EXISTS ${fullName};`;

      case 'select':
        return `SELECT *\nFROM ${fullName};`;

      case 'select-top':
        return `SELECT *\nFROM ${fullName}\nLIMIT ${SELECT_TOP_ROW_LIMIT};`;

      case 'insert': {
        const columns = ctx.metadata?.columns?.filter((col) => !col.isIdentity);
        if (!columns?.length) {
          return `INSERT INTO ${fullName} (column1, column2, ...)\nVALUES (value1, value2, ...);`;
        }

        const columnList = columns.map((col) => quoteIdentifier(col.name)).join(', ');
        const values = columns.map(formatInsertPlaceholder).join(', ');
        return `INSERT INTO ${fullName} (${columnList})\nVALUES (${values});`;
      }

      case 'update': {
        const columns = ctx.metadata?.columns ?? [];
        if (columns.length === 0) {
          return `UPDATE ${fullName}\nSET column1 = value1,\n    column2 = value2\nWHERE <search_condition>;`;
        }

        const pkColumns = columns.filter((col) => col.isPrimaryKey);
        const updateColumns = columns.filter((col) => !col.isPrimaryKey && !col.isIdentity);

        const setClauses = updateColumns
          .slice(0, 3)
          .map((col) => `  ${quoteIdentifier(col.name)} = <value>`)
          .join(',\n');

        const whereClause = pkColumns.length > 0
          ? pkColumns.map((col) => `${quoteIdentifier(col.name)} = <value>`).join(' AND ')
          : '<search_condition>';

        return `UPDATE ${fullName}\nSET\n${setClauses}\nWHERE ${whereClause};`;
      }

      case 'delete': {
        const columns = ctx.metadata?.columns ?? [];
        const pkColumns = columns.filter((col) => col.isPrimaryKey);
        const whereClause = pkColumns.length > 0
          ? pkColumns.map((col) => `${quoteIdentifier(col.name)} = <value>`).join(' AND ')
          : '<search_condition>';

        return `DELETE FROM ${fullName}\nWHERE ${whereClause};`;
      }

      default:
        throw new Error(`Unknown table script type: ${type}`);
    }
  },

  generateDatabaseScript(type: DatabaseScriptType, ctx: DatabaseScriptContext): string {
    const name = quoteIdentifier(ctx.databaseName);

    switch (type) {
      case 'create':
        return `-- SQLite databases are files.\n-- Create a new database by connecting to a new .sqlite file path.\n-- (No CREATE DATABASE statement exists in SQLite)\n-- Target: ${name}`;

      case 'drop':
        return `-- SQLite databases are files.\n-- To delete a database, close the connection and delete the .sqlite file.\n-- Target: ${name}`;

      case 'use':
        return `-- SQLite does not support USE.\n-- To work with another database, connect to a different file or ATTACH DATABASE.\n-- Current: ${name}`;

      default:
        throw new Error(`Unknown database script type: ${type}`);
    }
  },

  generateNewTableTemplate(ctx: NewObjectTemplateContext): string {
    const db = ctx.database ? ` (${ctx.database})` : '';
    return `-- Create new SQLite table${db}\nCREATE TABLE "\${1:TableName}" (\n  "\${2:id}" INTEGER PRIMARY KEY,\n  "\${3:column1}" TEXT NOT NULL\n);\n`;
  },

  generateNewViewTemplate(ctx: NewObjectTemplateContext): string {
    const db = ctx.database ? ` (${ctx.database})` : '';
    return `-- Create new SQLite view${db}\nCREATE VIEW "\${1:ViewName}" AS\nSELECT \${2:*}\nFROM "\${3:TableName}";\n`;
  },

  generateNewProcedureTemplate(): string {
    return `-- SQLite does not support stored procedures.`;
  },

  generateNewFunctionTemplate(): string {
    return `-- SQLite does not support CREATE FUNCTION in the same way as server databases.`;
  },

  generateNewDatabaseTemplate(): string {
    return `-- SQLite databases are files.\n-- Create a new database by connecting to a new .sqlite file path.`;
  },
};
